/**
 * Cihaz sürüm (versiyon) karşılaştırma yardımcıları.
 *
 * Sahadan gelen sürüm metinleri tek biçimde değildir:
 *   "2.9.2"      ekran (BeverageMachine APK)
 *   "1.2.3.(4)"  eski ekran sürümleri (parantezli build)
 *   "V1.0.0"     donanım
 *   "C1.0.0"     Fincanda anakart      "T1.0.0"  Çayset kartı
 *   "CG1.0.0"    değirmen kartı        "CHA1.0.1" askı 1 (CHB/CHC: askı 2-3)
 *   "11"         Android sürümü
 *
 * Bu yüzden karşılaştırma harf öneklerini ve parantezleri yok sayar, yalnızca
 * sayı gruplarını soldan sağa kıyaslar. Eksik segmentler 0 kabul edilir
 * ("1.2" === "1.2.0"). Sürüm okunamıyorsa (sayı içermiyorsa) karşılaştırma
 * null döner — çağıran taraf bunu "eşleşmedi" olarak yorumlar.
 */

import {
  getDeviceCards,
  formatCardVersionWithModel,
  getCardModelShortLabel,
  normalizeCardModel,
} from "./deviceCards";

// Server bazı alanlarda değer yoksa metin fallback'i döndürebiliyor; bunlar
// "sürüm bildirilmemiş" sayılır.
const EMPTY_TOKENS = new Set([
  "",
  "-",
  "n/a",
  "na",
  "yok",
  "bilinmiyor",
  "belirtilmemiş",
  "belirtilmemis",
  "null",
  "undefined",
]);

/** Ham değeri temizler; sürüm bildirilmemişse boş string döner. */
export const normalizeVersion = (raw) => {
  if (raw === null || raw === undefined) return "";
  const text = String(raw).trim();
  if (EMPTY_TOKENS.has(text.toLowerCase())) return "";
  return text;
};

/**
 * "CHA1.0.1" → [1, 0, 1] · "1.2.3.(4)" → [1, 2, 3] · okunamazsa null.
 *
 * PARANTEZ İÇİ SÜRÜM SEGMENTİ DEĞİLDİR (2026-09-14 düzeltmesi): eski ekran
 * sürümleri "2.9.2.(15)" biçiminde gelir ve parantez içi BUILD numarasıdır.
 * Önceden 4. segment sayılıyordu; sonuç olarak sahadaki "2.9.2.(15)" cihazı
 * "= 2.9.2" filtresine HİÇ düşmüyor, üstelik "> 2.9.2" sayılıyordu. Yani
 * "2.9.2 ve altını bul" diyen kullanıcı, güncellenmesi gereken cihazları
 * sessizce kaçırıyordu. Parantezli bölüm artık atılır.
 */
export const parseVersionParts = (raw) => {
  const text = normalizeVersion(raw);
  if (!text) return null;
  const withoutBuild = text.replace(/\([^)]*\)/g, " ");
  const numbers = withoutBuild.match(/\d+/g);
  if (!numbers || numbers.length === 0) return null;
  return numbers.map(Number);
};

/**
 * İki sürümü kıyaslar: -1 (a < b), 0 (a === b), 1 (a > b).
 * Taraflardan biri okunamazsa null döner.
 */
export const compareVersions = (a, b) => {
  const left = parseVersionParts(a);
  const right = parseVersionParts(b);
  if (!left || !right) return null;

  const length = Math.max(left.length, right.length);
  for (let i = 0; i < length; i += 1) {
    const x = left[i] ?? 0;
    const y = right[i] ?? 0;
    if (x < y) return -1;
    if (x > y) return 1;
  }
  return 0;
};

/**
 * Rapor satırındaki sürüm alanları — filtre ve tablo tek listeden beslenir.
 *
 * KART ALANLARI (`cardType` taşıyanlar) DÜZ ALANDAN OKUNMAZ (Workspace-#38):
 * değer `utils/deviceCards.getDeviceCards(row)` ile gelir — satırda `cards`
 * doluysa o, değilse düz alanlardan (mainboardVersion, hangerVersions …)
 * türetilmiş hali. Böylece eski/bayat kayıt yorumu cihaz detayındaki
 * "VERSİYONLAR" kartıyla BİREBİR aynıdır. `id`'ler geriye uyum için eski düz
 * alan adlarını korur (kriter nesneleri bu id'yi taşır).
 *
 * `kind: "model"` alanları kartın BİLDİRDİĞİ model numarasına göre süzer
 * (ör. "Askı modeli = 41" → G18A askılar). Model bildirmeyen kart (null)
 * "Bildirilmemiş" sayılır.
 */
export const VERSION_FIELDS = [
  { id: "appVersion", label: "Ekran (Uygulama)", short: "Ekran" },
  { id: "uiVersion", label: "Arayüz", short: "Arayüz" },
  { id: "osVersion", label: "İşletim Sistemi", short: "İşletim Sistemi" },
  { id: "hwVersion", label: "Donanım", short: "Donanım" },
  {
    id: "mainboardVersion",
    label: "Fincanda Anakart",
    short: "Fincanda Anakart",
    cardType: "fincandaMainBoard",
  },
  {
    id: "caysetVersion",
    label: "Çayset Anakart",
    short: "Çayset Anakart",
    cardType: "caysetMainBoard",
  },
  {
    id: "grinderVersion",
    label: "Fincanda Değirmen",
    short: "Fincanda Değirmen",
    cardType: "fincandaGrinder",
  },
  {
    id: "hangerVersions",
    label: "Fincanda Askı (herhangi biri)",
    short: "Fincanda Askı",
    cardType: "fincandaHanger",
    isList: true,
  },
  {
    id: "mainboardModel",
    label: "Fincanda Anakart Modeli",
    short: "Fincanda Anakart modeli",
    cardType: "fincandaMainBoard",
    kind: "model",
    placeholder: "örn. 24",
  },
  {
    id: "caysetModel",
    label: "Çayset Anakart Modeli",
    short: "Çayset Anakart modeli",
    cardType: "caysetMainBoard",
    kind: "model",
    placeholder: "örn. 10",
  },
  {
    id: "grinderModel",
    label: "Fincanda Değirmen Modeli",
    short: "Fincanda Değirmen modeli",
    cardType: "fincandaGrinder",
    kind: "model",
    placeholder: "örn. 50",
  },
  {
    id: "hangerModels",
    label: "Fincanda Askı Modeli (herhangi biri)",
    short: "Fincanda Askı modeli",
    cardType: "fincandaHanger",
    kind: "model",
    isList: true,
    placeholder: "örn. 41",
  },
];

export const getVersionField = (fieldId) =>
  VERSION_FIELDS.find((field) => field.id === fieldId) || null;

export const isModelField = (field) => field?.kind === "model";

/** Filtre operatörleri. `needsValue: false` olanlar sürüm girdisi istemez. */
export const VERSION_OPERATORS = [
  { id: "lt", label: "Küçük (<)", symbol: "<", needsValue: true },
  { id: "lte", label: "Küçük veya eşit (≤)", symbol: "≤", needsValue: true },
  { id: "eq", label: "Eşit (=)", symbol: "=", needsValue: true },
  { id: "neq", label: "Eşit değil (≠)", symbol: "≠", needsValue: true },
  { id: "gte", label: "Büyük veya eşit (≥)", symbol: "≥", needsValue: true },
  { id: "gt", label: "Büyük (>)", symbol: ">", needsValue: true },
  { id: "empty", label: "Bildirilmemiş", symbol: "boş", needsValue: false },
  { id: "notEmpty", label: "Bildirilmiş", symbol: "dolu", needsValue: false },
];

export const getVersionOperator = (operatorId) =>
  VERSION_OPERATORS.find((operator) => operator.id === operatorId) || null;

/**
 * Model numarası kategoriktir (40 J18A, 41 G18A): büyük/küçük kıyası anlamsız,
 * yalnız eşitlik ve bildirilmiş/bildirilmemiş sunulur.
 */
const MODEL_OPERATOR_IDS = new Set(["eq", "neq", "empty", "notEmpty"]);

/** Alanın kullanabileceği operatörler (model alanlarında sıralama yok). */
export const getOperatorsForField = (fieldId) =>
  isModelField(getVersionField(fieldId))
    ? VERSION_OPERATORS.filter((operator) => MODEL_OPERATOR_IDS.has(operator.id))
    : VERSION_OPERATORS;

/** Model girdisi: ilk tam sayı ("41", "41 G18A" → 41); okunamazsa null. */
export const parseModelValue = (raw) => {
  const match = normalizeVersion(raw).match(/\d+/);
  if (!match) return null;
  return normalizeCardModel(match[0]);
};

/** Kriter değeri alanın türüne göre geçerli mi (sürüm / model no). */
export const isCriterionValueValid = (fieldId, value) =>
  isModelField(getVersionField(fieldId))
    ? parseModelValue(value) !== null
    : parseVersionParts(value) !== null;

/** Tek bir sürüm değerini operatör + hedef sürüme göre test eder. */
export const matchesVersionValue = (rawValue, operatorId, target) => {
  const value = normalizeVersion(rawValue);

  if (operatorId === "empty") return !value;
  if (operatorId === "notEmpty") return !!value;

  if (!value) return false;

  // EŞİTLİKTE KULLANICININ YAZDIĞI KADAR SEGMENT KIYASLANIR (2026-09-14):
  // "= 2.9" yazan kullanıcı 2.9 AİLESİNİ kastediyor; önceden eksik segmentler
  // 0 sayıldığı için yalnızca tam "2.9.0" eşleşiyor, 2.9.2 elenip liste boş
  // geliyordu. Sıralama operatörlerinde (</>) davranış değişmez — orada eksik
  // segmentin 0 sayılması doğrudur.
  if (operatorId === "eq" || operatorId === "neq") {
    const left = parseVersionParts(value);
    const right = parseVersionParts(target);
    if (!left || !right) return false;
    const samePrefix = right.every((part, i) => (left[i] ?? 0) === part);
    return operatorId === "eq" ? samePrefix : !samePrefix;
  }

  const result = compareVersions(value, target);
  if (result === null) return false;

  switch (operatorId) {
    case "lt":
      return result < 0;
    case "lte":
      return result <= 0;
    case "eq":
      return result === 0;
    case "neq":
      return result !== 0;
    case "gte":
      return result >= 0;
    case "gt":
      return result > 0;
    default:
      return false;
  }
};

/** Bir kriterin uygulanabilir olması için alan + operatör + (gerekiyorsa) değer şart. */
export const isVersionCriterionReady = (criterion) => {
  if (!criterion?.field || !criterion?.operator) return false;
  const operator = getVersionOperator(criterion.operator);
  if (!operator) return false;
  if (!getOperatorsForField(criterion.field).includes(operator)) return false;
  if (!operator.needsValue) return true;
  return isCriterionValueValid(criterion.field, criterion.value);
};

/**
 * Alanın satırdaki dolu değerleri.
 *   - kart alanı: getDeviceCards(row) içinde o tipteki kartların sürümleri
 *     (veya bildirilmiş model numaraları)
 *   - diğerleri: düz alan (tek değer)
 */
const getFieldValues = (item, field) => {
  if (field.cardType) {
    const cards = getDeviceCards(item).filter(
      (card) => card.type === field.cardType
    );
    return isModelField(field)
      ? cards.map((card) => card.model).filter((model) => model !== null)
      : cards.map((card) => card.version).filter(Boolean);
  }
  const value = normalizeVersion(item?.[field.id]);
  return value ? [value] : [];
};

/**
 * Rapor satırını tek kritere göre test eder.
 * Kart alanlarında (askı gibi birden fazla olabilen) mantık şudur:
 *   - "Bildirilmemiş": o tipteki hiçbir kart değer bildirmemişse eşleşir
 *   - diğer operatörler: kartlardan HERHANGİ biri koşulu sağlarsa eşleşir
 */
export const matchesVersionCriterion = (item, criterion) => {
  if (!isVersionCriterionReady(criterion)) return true;

  const field = getVersionField(criterion.field);
  if (!field) return true;

  const values = getFieldValues(item, field);

  if (criterion.operator === "empty") return values.length === 0;
  if (criterion.operator === "notEmpty") return values.length > 0;

  if (isModelField(field)) {
    const target = parseModelValue(criterion.value);
    return values.some((model) =>
      criterion.operator === "eq" ? model === target : model !== target
    );
  }

  return values.some((value) =>
    matchesVersionValue(value, criterion.operator, criterion.value)
  );
};

/** Tüm kriterler VE'lenir. */
export const matchesVersionCriteria = (item, criteria = []) =>
  criteria.every((criterion) => matchesVersionCriterion(item, criterion));

/** Kriteri chip/özet metnine çevirir: "Ekran < 2.9.2". */
export const describeVersionCriterion = (criterion) => {
  const field = getVersionField(criterion?.field);
  const operator = getVersionOperator(criterion?.operator);
  if (!field || !operator) return "";
  if (!operator.needsValue) return `${field.short} ${operator.label}`;
  if (isModelField(field)) {
    const model =
      getCardModelShortLabel(parseModelValue(criterion.value)) ||
      normalizeVersion(criterion.value);
    return `${field.short} ${operator.symbol} ${model}`;
  }
  return `${field.short} ${operator.symbol} ${normalizeVersion(criterion.value)}`;
};

/**
 * Rapor satırındaki dolu sürümleri tablo/Excel için tek listeye çevirir.
 * Kart sürümleri getDeviceCards'tan gelir ve modeli yanında taşır
 * ("1.2.2 (41 G18A)"); askılar "Askı 1/2/3" (port) olarak ayrı satırlara açılır.
 * Model alanları ayrı satır üretmez — model zaten sürümün yanında.
 */
export const getVersionRows = (item) => {
  const rows = [];
  const cards = getDeviceCards(item);

  VERSION_FIELDS.forEach((field) => {
    if (isModelField(field)) return;

    if (field.cardType) {
      cards
        .filter((card) => card.type === field.cardType)
        .forEach((card) => {
          const value = formatCardVersionWithModel(card);
          if (!value) return;
          rows.push({
            label: field.isList ? `${field.short} ${card.port}` : field.short,
            value,
          });
        });
      return;
    }

    const version = normalizeVersion(item?.[field.id]);
    if (version) rows.push({ label: field.short, value: version });
  });

  return rows;
};
